import dayjs from 'dayjs'
import { Form, Formik, useFormik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Button from '../../../components/Button'
import Checkbox from '../../../components/Checkbox'
import CustomLink from '../../../components/CustomLink'
import Input from '../../../components/Input'
import Loader from '../../../components/Loader'
import Message from '../../../components/Message'
import DialogModal from '../../../components/modals/DialogModal'
import { useAppSelector } from '../../../store'
import { NewUserState, setNewUserField } from '../../../store/newUser'
import ScreenHeader from '../../../components/ScreenHeader'
import ProgressBar from '../../../components/ProgressBar'
import { useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
//
const SignUpCreatePassword = () => {
  //
  const { t } = useTranslation()
  //
  const dispatch = useDispatch()
  //
  const { personal } = useAppSelector((data: { newUser: NewUserState }) => data.newUser)
  //
  const [openDialogModal, setOpenDialogModal] = useState<boolean>(false)
  //
  // login mutation
  const [login, { isLoading }] = api.useLoginMutation()
  //
  const [signUp, { isLoading: isSignUpLoading, isSuccess }] = api.useSignUpMutation()
  //
  const navigate = useNavigate()
  //
  //
  const Registration = async () => {
    //
    // Convert the input date string to a JavaScript Date object
    const dateParts = (personal.birthday && (personal.birthday as unknown as string))
      ?.split('/')
      .map((part) => part.trim())
    const formattedDate = dateParts && new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
    // Format the date to "YYYY-MM-DD"
    const formattedDateString = formattedDate && formattedDate?.toISOString()?.split('T')[0]
    //

    if (
      personal &&
      personal.terms === true &&
      personal.password &&
      personal.confirmPassword &&
      Object.keys(personal.password).length !== 0 &&
      personal.gender &&
      personal.citizenship &&
      personal.birthday &&
      formattedDateString &&
      Object.keys(personal).length !== 0
    ) {
      try {
        await signUp({
          firstName: personal.firstName!,
          lastName: personal.lastName!,
          firstNameGe: personal.firstNameGe,
          lastNameGe: personal.lastNameGe,
          phone: personal.phone!,
          personalNumber: personal.personalNumber || null,
          passportNumber: personal.passportNumber || null,
          email: personal.email!,
          password: personal.password!,
          passwordConfirm: personal.confirmPassword!,
          birthday: dayjs(formattedDateString).format('YYYY-MM-DD'),
          citizenship: personal.citizenship,
          gender: personal.gender,
          consent: personal.terms!,
          sgsVerification: personal.sgsVerification,
        }).unwrap()

        toast.success(`${t('accountRegistered')}`)
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')}`)
        }
      }
    }
  }
  //
  //
  // yup validations schema
  const PersonalInfoValidationSchemaGe = Yup.object({
    password: Yup.string()
      .min(8, `${t('minPassLenght')}`)
      .matches(/[0-9]/, `${t('passContainNumber')}`)
      .matches(/[a-z]/, `${t('passContainLowerCase')}`)
      .matches(/[a-zA-Z]/, `${t('passContainEnglishLetters')}`)
      .matches(/[A-Z]/, `${t('passContainUpperCase')}`)
      .required(`${t('required')}`),

    // .matches(/[^\w]/, 'პაროლი უნდა შეიცავდეს სიმბოლოს'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], `${t('enterSamePass')}`)
      .required(`${t('required')}`),

    terms: Yup.boolean().oneOf([true], `${t('tickCheckbox')}`),
  })
  // initial values
  const initialValues = {
    password: personal.password || '',
    confirmPassword: personal.confirmPassword || '',
    terms: personal.terms || false,
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: PersonalInfoValidationSchemaGe,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false)
    },
  })
  //
  //
  const autoLoginCallback = useCallback(() => {
    if (personal && personal.email && personal.password && isSuccess) {
      try {
        login({ email: personal.email, password: personal.password }).unwrap()
        navigate(`/my-policies`)
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error('Please fill all required fields')
        }
      }
    }
  }, [login, navigate, personal, isSuccess])

  //
  useEffect(() => {
    autoLoginCallback()
    //
    return () => {}
  }, [autoLoginCallback])

  //
  //
  return (
    <MainWrapper>
      {isSignUpLoading || isLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={PersonalInfoValidationSchemaGe}
          enableReinitialize={true}
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(false)
          }}
        >
          {({ handleSubmit, handleBlur, setFieldValue, touched, errors, setTouched }) => (
            <Form onSubmit={handleSubmit}>
              <Helmet>
                <title>{t('createPassword')}</title>
                <meta name="description" content={`${t('createPassword')}`} />
                <link rel="canonical" href={window.location.href} />
              </Helmet>

              <ScreenHeader text={`${t('homePage')}`} url="/" />
              <ProgressBar nextButton={false} prevButton={false} stepNumber={2} totalCount={2} filledCount={2} />
              <LargeTitle>{t('createPassword')}</LargeTitle>
              <Wrapper>
                <InfoBlock>
                  <Input
                    id="password"
                    label={`${t('password')}`}
                    type="password"
                    value={personal.password || ''}
                    onChange={(value) => [
                      dispatch(
                        setNewUserField({
                          fieldName: 'password',
                          fieldValue: value,
                        }),
                      ),
                      setFieldValue('password', value),
                    ]}
                    width={'fullWidth'}
                    style={{ marginBottom: '24px' }}
                    icon={true}
                    forSpreadOperator={false}
                    error={(touched.password || personal.password) && errors.password ? true : false}
                    errorText={errors.password}
                    name="password"
                    onBlur={handleBlur}
                  />

                  <Input
                    id="confirm-password"
                    label={`${t('repeatPassword')}`}
                    type="password"
                    value={personal.confirmPassword || ''}
                    onChange={(value) => [
                      dispatch(
                        setNewUserField({
                          fieldName: 'confirmPassword',
                          fieldValue: value,
                        }),
                      ),
                      setFieldValue('confirmPassword', value),
                    ]}
                    width={'fullWidth'}
                    style={{ marginBottom: '30px' }}
                    icon={true}
                    forSpreadOperator={false}
                    error={
                      (touched.confirmPassword || personal.confirmPassword) && errors.confirmPassword ? true : false
                    }
                    errorText={errors.confirmPassword}
                    name="confirmPassword"
                    onBlur={handleBlur}
                  />

                  <Message variant="gray" text={`${t('passwordText')}`} />
                  <br />
                  {openDialogModal === true ? null : (
                    <Checkbox
                      id="for-me-frani-terms"
                      label={`${t('allTermsAcceptext')}`}
                      defaultChecked={personal.terms}
                      onClick={() => setOpenDialogModal(true)}
                      notChecke={true}
                      error={(touched.terms || personal.terms) && errors.terms ? true : false}
                      onChange={(checked) => {
                        if (checked === true) {
                          dispatch(
                            setNewUserField({
                              fieldName: 'terms',
                              fieldValue: true,
                            }),
                          )
                          setFieldValue('terms', true)
                        } else {
                          dispatch(
                            setNewUserField({
                              fieldName: 'terms',
                              fieldValue: false,
                            }),
                          )
                          setFieldValue('terms', false)
                        }
                      }}
                    />
                  )}

                  <DialogModal
                    src={`${document.location.origin}/consent-dialog`}
                    onClose={() => setOpenDialogModal(false)}
                    isOpen={openDialogModal}
                  >
                    <div>
                      <br />
                      <br />
                      <Checkbox
                        defaultChecked={personal.terms}
                        id="registration-dialog"
                        label={`${t('allTermsAcceptext')}`}
                        onChange={(checked) => {
                          if (checked === true) {
                            dispatch(
                              setNewUserField({
                                fieldName: 'terms',
                                fieldValue: true,
                              }),
                            )
                            setFieldValue('terms', true)
                            setOpenDialogModal(false)
                          } else {
                            dispatch(
                              setNewUserField({
                                fieldName: 'terms',
                                fieldValue: false,
                              }),
                            )
                            setFieldValue('terms', false)
                            setOpenDialogModal(false)
                          }
                        }}
                      />
                      <br />
                    </div>
                  </DialogModal>

                  <br />
                  <br />

                  <Button
                    onClick={() => {
                      setTouched({
                        password: true,
                        confirmPassword: true,
                        terms: true,
                      })

                      formik.validateForm().then((errors) => {
                        if (Object.keys(errors).length === 0) {
                          Registration()
                        }
                      })
                    }}
                    text={`${t('registration')}`}
                    btnStyleType="primary"
                    fullWidth={true}
                    disabled={isSuccess}
                  />
                </InfoBlock>

                <NavigateButtons>
                  <div className="d-flex justify-content-center align-items-center mb-1">
                    <CustomLink to={'/auth/personal-info'} size={'medium'} leftLineIcon={true} text={`${t('back')}`} />
                  </div>
                </NavigateButtons>
              </Wrapper>
            </Form>
          )}
        </Formik>
      )}
    </MainWrapper>
  )
}

// styles
const NavigateButtons = styled.div`
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  left: 0;
  bottom: 22px;
  z-index: 2;
  height: 24px;
  ${Responsive.tablet} {
    bottom: 12px;
  }
  ${Responsive.mobile} {
    bottom: 12px;
  }
`
const Wrapper = styled.div`
  background-color: ${Colors.white};
  max-width: 528px;
  width: 100%;
  border-radius: 8px;
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;

  /* box shadow */
  box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  -webkit-box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  -moz-box-shadow: 0px 0px 7px -1px rgba(0, 0, 0, 0.02);
  &:hover {
    box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
    -webkit-box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
    -moz-box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.02);
  }

  /* responsive */
  ${Responsive.tablet} {
  }
  ${Responsive.mobile} {
    width: 100%;
  }
`
const InfoBlock = styled.div`
  width: 100%;
  padding: 50px 64px;
  /* h3 title */
  h3 {
    font-size: 18px;
    color: ${Colors.Black};
    text-align: center;
    text-transform: capitalize;
  }

  /* p descirption */
  p {
    font-size: 14px;
    line-height: 22px;
    color: ${Colors.SilverChalice};
    text-align: center;
    margin-bottom: 45px;
  }

  ${Responsive.mobile} {
    padding: 32px 24px;
    /* h3 title */
    h3 {
      font-size: 20px;
      margin-bottom: 15px;
    }

    /* p descirption */
    p {
      margin-bottom: 27px;
    }
  }
`

const LargeTitle = styled.h1`
  font-size: 36px;
  line-height: 40px;
  color: ${Colors.primaryBlack};
  text-align: center;
  margin-top: 56px;
  margin-bottom: 56px;
  text-transform: capitalize;

  /* responsive */
  ${Responsive.tablet} {
    font-size: 24px;
    line-height: 26px;
  }

  /* responsive */
  ${Responsive.mobile} {
    font-size: 18px;
    line-height: 20px;
    margin-bottom: 56px;
    margin-top: 56px;
  }
`

const MainWrapper = styled.div`
  padding-bottom: 64px;
  margin-bottom: 64px;
  background-color: ${Colors.backgroundGrey};
  ${Responsive.tablet} {
    padding-bottom: 80px;
    margin-bottom: 0px;
  }
  ${Responsive.mobile} {
    padding-bottom: 80px;
    margin-bottom: 0px;
  }
`
export default SignUpCreatePassword
